"use client";

import { sendGTMEvent } from "@next/third-parties/google";

import { mixpanelTrack } from "@/shared/analytics/mixpanel";

interface ViewItemParams {
  userId?: string | number;
  itemId: number;
  itemName: string;
  price: number;
  userGroup?: string | null;
  pageName?: string;
  extra?: Record<string, unknown>;
}

export function trackViewItem({
  userId,
  itemId,
  itemName,
  price,
  userGroup,
  pageName,
  extra,
}: ViewItemParams): void {
  mixpanelTrack("view_item", {
    ...(userId != null && { distinct_id: String(userId) }),
    currency: "KRW",
    item_id: itemId,
    item_name: itemName,
    price,
    value: price,
    userGroup: userGroup ?? null,
    ...(pageName && { name: pageName }),
    ...extra,
  });
  sendGTMEvent({
    event: "view_item",
    ecommerce: {
      currency: "KRW",
      value: price,
      items: [
        {
          item_id: itemId,
          item_name: itemName,
          price,
          quantity: 1,
        },
      ],
    },
  });
}

export function trackViewBrand({
  userId,
  brandId,
  brandName,
}: {
  userId?: string | number;
  brandId: number;
  brandName: string;
}): void {
  mixpanelTrack("view_brand", {
    ...(userId != null && { distinct_id: String(userId) }),
    name: brandName,
    id_brand: brandId,
  });
}

export function trackClickSubmitRefund({
  userId,
  body,
}: {
  userId?: string | number;
  body: Record<string, unknown>;
}): void {
  mixpanelTrack("click_submit_refund", {
    ...(userId != null && { distinct_id: String(userId) }),
    body,
  });
}

// ── 핵심 커머스 퍼널 ──
// 레거시는 Cart View / Payment View / Order Complete View 페이지뷰로 퍼널을
// 근사했다. 새 프론트는 같은 페이지뷰를 유지하면서 명시적 퍼널 이벤트도
// 기록해 이벤트 대시보드에서 stage 별 drop-off 를 직접 측정 가능하게 한다.

interface CartLineItem {
  itemId: number;
  itemName: string;
  price: number;
  quantity: number;
  brandId?: number | null;
  brandName?: string | null;
  optionDetailId?: number | null;
}

export function trackAddToCart({
  userId,
  item,
  source,
}: {
  userId?: string | number;
  item: CartLineItem;
  source?: string;
}): void {
  const value = item.price * item.quantity;
  mixpanelTrack("add_to_cart", {
    ...(userId != null && { distinct_id: String(userId) }),
    currency: "KRW",
    item_id: item.itemId,
    item_name: item.itemName,
    price: item.price,
    quantity: item.quantity,
    value,
    id_brand: item.brandId ?? null,
    brand_name: item.brandName ?? null,
    option_detail_id: item.optionDetailId ?? null,
    ...(source && { source }),
  });
  // 레거시 GTM dataLayer 연속성 — product-details/*Select.js 가 쏘던 동일 shape.
  sendGTMEvent({
    event: "add_to_cart",
    ecommerce: {
      currency: "KRW",
      value,
      items: [
        {
          item_id: item.itemId,
          item_name: item.itemName,
          price: item.price,
          quantity: item.quantity,
        },
      ],
    },
  });
}

export function trackBeginCheckout({
  userId,
  items,
  totalValue,
}: {
  userId?: string | number;
  items: CartLineItem[];
  totalValue: number;
}): void {
  mixpanelTrack("begin_checkout", {
    ...(userId != null && { distinct_id: String(userId) }),
    currency: "KRW",
    num_items: items.length,
    total_quantity: items.reduce((sum, i) => sum + i.quantity, 0),
    value: totalValue,
    item_ids: items.map((i) => i.itemId),
  });
  sendGTMEvent({
    event: "begin_checkout",
    ecommerce: {
      currency: "KRW",
      value: totalValue,
      items: items.map((i) => ({
        item_id: i.itemId,
        item_name: i.itemName,
        price: i.price,
        quantity: i.quantity,
      })),
    },
  });
}

export function trackPurchase({
  userId,
  orderId,
  orderNo,
  items,
  totalValue,
}: {
  userId?: string | number;
  orderId: number;
  orderNo: string;
  items: CartLineItem[];
  totalValue: number;
}): void {
  mixpanelTrack("purchase", {
    ...(userId != null && { distinct_id: String(userId) }),
    currency: "KRW",
    order_id: orderId,
    order_no: orderNo,
    num_items: items.length,
    total_quantity: items.reduce((sum, i) => sum + i.quantity, 0),
    value: totalValue,
    item_ids: items.map((i) => i.itemId),
  });
  sendGTMEvent({
    event: "purchase",
    ecommerce: {
      transaction_id: orderNo,
      currency: "KRW",
      value: totalValue,
      items: items.map((i) => ({
        item_id: i.itemId,
        item_name: i.itemName,
        price: i.price,
        quantity: i.quantity,
      })),
    },
  });
}

/**
 * 레거시 GTM dataLayer 연속성 — `pages/register/register.js:316` 에서 회원가입
 * 성공 시 쏘던 이벤트를 그대로 재현. Mixpanel 쪽은 identify 로 커버되므로
 * 여기서는 dataLayer 만 푸시한다.
 */
export function trackSignUp({
  userId,
  userName,
}: {
  userId: number | string;
  userName?: string | null;
}): void {
  sendGTMEvent({
    event: "sign_up",
    eventModel: {
      user_id: userId,
      ...(userName != null && { user_name: userName }),
    },
  });
}

/**
 * 레거시 `_app.js:119` 에서 로그인 성공 시 dataLayer 에 user_id 를 바인딩했다.
 * GTM 컨테이너의 후속 이벤트들이 이 값을 기반으로 사용자 속성을 매핑한다.
 */
export function bindGtmUser(userId: number | string): void {
  sendGTMEvent({ user_id: userId });
}
